import React, { useState } from "react";
import { PageLayout } from "../ui-components/PageLayout";
import {
  Select,
  MenuItem,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Grid2,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FinancialLineChartWrapper } from "../ui-components/Charts/FinancialLineChartWrapper";
import { RootState } from "../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { getFinancialCategoryDataSummary } from "../store/modules/analytics/analyticsActions";
import { AppDispatch } from "../store/store";
import LoadingDialog from "../ui-components/Dialogs/LoadingDialog";
import { YearFilterSelect } from "../ui-components/YearFilterSelect";
import { useQuery } from "@tanstack/react-query";
import { FinancialCategoryEnumModel } from "../types/types";
import { CoAgentFilterSelect } from "../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../hooks/usePageCoAgentFilter";

const financialCategories = [
  FinancialCategoryEnumModel.Commission,
  FinancialCategoryEnumModel.Gci,
  FinancialCategoryEnumModel.EffectiveGci,
  FinancialCategoryEnumModel.BrokerageTotal,
  FinancialCategoryEnumModel.NetCommissionIncome,
  FinancialCategoryEnumModel.NetIncome,
  FinancialCategoryEnumModel.NetIncomePercent,
] as const;

const financialCategoryLabels = {
  [FinancialCategoryEnumModel.Commission]: "Average Commission Percentage",
  [FinancialCategoryEnumModel.Gci]: "GCI",
  [FinancialCategoryEnumModel.EffectiveGci]: "Effective GCI",
  [FinancialCategoryEnumModel.BrokerageTotal]: "Brokerage Total Fees",
  [FinancialCategoryEnumModel.NetCommissionIncome]: "Net Commission Income",
  [FinancialCategoryEnumModel.NetIncome]: "Net Income",
  [FinancialCategoryEnumModel.NetIncomePercent]: "Average Income Percentage",
} as const satisfies Record<FinancialCategoryEnumModel, string>;

const financialCategoryDescriptions = {
  [FinancialCategoryEnumModel.Commission]:
    "This average excludes rentals and referrals out.",
  [FinancialCategoryEnumModel.Gci]: undefined,
  [FinancialCategoryEnumModel.EffectiveGci]:
    " GCI received by brokerage after client rebates and referral fees, if any.",
  [FinancialCategoryEnumModel.BrokerageTotal]: undefined,
  [FinancialCategoryEnumModel.NetCommissionIncome]:
    "Commission income received by agent after paying brokerage fees",
  [FinancialCategoryEnumModel.NetIncome]:
    "Net Income represents your Net Commission Income minus any out-of-pocket expenses.",
  [FinancialCategoryEnumModel.NetIncomePercent]:
    "Percentage of net income compared to sale price. This average excludes rentals and referrals out.",
} as const satisfies Record<FinancialCategoryEnumModel, string | undefined>;
const FinancialTrends = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [selectedCategory, setSelectedCategory] =
    useState<FinancialCategoryEnumModel>(FinancialCategoryEnumModel.Commission);
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("financialTrends", selectedCoAgentIds);

  const selectedYearText =
    selectedYearIds.length > 0
      ? selectedYearIds
          .map((id) => years.find((y) => y.id === id)?.year)
          .join(", ")
      : "All Years";

  const [touchStart, setTouchStart] = useState(0);
  const handleTouchStart = (e: React.TouchEvent) =>
    setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      const currentIndex = financialCategories.indexOf(selectedCategory);
      const newIndex = diff > 0 ? currentIndex + 1 : currentIndex - 1;
      setSelectedCategory(
        financialCategories[
          Math.max(0, Math.min(newIndex, financialCategories.length - 1))
        ],
      );
    }
  };

  const { financialCategoryState } = useSelector(
    (state: RootState) => state.analytics,
  );
  const loading = financialCategoryState.loading;

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: FinancialCategoryEnumModel,
  ) => {
    setSelectedCategory(newValue);
  };

  useQuery({
    queryKey: [
      "getFinancialCategoryDataSummary",
      selectedCategory,
      selectedYearIds,
      effectiveCoAgentIds,
    ],
    queryFn: () => {
      dispatch(
        getFinancialCategoryDataSummary(
          selectedYearIds,
          selectedCategory,
          effectiveCoAgentIds,
        ),
      );
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
    enabled: selectedCategory !== undefined,
  });

  return (
    <PageLayout>
      <LoadingDialog open={loading} message="Loading Financial Data..." />
      <Box sx={{ mb: 6, ml: 1 }}>
        <Typography
          variant="h4"
          color="primary"
          component="h1"
          fontWeight="bold"
          gutterBottom
        >
          Financial Trends
        </Typography>

        <Box sx={{ mb: 4, mt: 2 }}>
          <Grid2 container spacing={3}>
            <Grid2 size={12}>
              <YearFilterSelect
                years={years}
                selectedYearIds={selectedYearIds}
                onChange={(value) => setSelectedYearIds(value)}
                label="Year"
                sx={{ mb: 2 }}
              />
            </Grid2>
            {!isGlobalFilterActive &&
              !hiddenFields?.some((field) => field.name === "coAgent") && (
                <Grid2 size={12}>
                  <CoAgentFilterSelect
                    coAgents={coAgents}
                    selectedCoAgentIds={effectiveCoAgentIds}
                    onChange={setPageCoAgentIds}
                    label="Filter by Team Members"
                    size="medium"
                  />
                </Grid2>
              )}
          </Grid2>
        </Box>

        {useMediaQuery(useTheme().breakpoints.up("sm")) ? (
          <Box
            sx={{
              background: "#F7F9FB",
              borderRadius: "8px",
              px: 2,
              py: 1,
              mb: 3,
              display: "flex",
              alignItems: "center",
              minHeight: 56,
            }}
          >
            <Tabs
              value={selectedCategory}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              sx={{
                minHeight: 40,
                "& .MuiTabs-flexContainer": { gap: 0 },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  minHeight: 40,
                  minWidth: 120,
                  borderRadius: "8px 8px 0 0",
                  color: "#222",
                  background: "transparent",
                  "&.Mui-selected": {
                    background: "#fff",
                    color: "#1976d2",
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {financialCategories.map((category) => (
                <Tab
                  label={financialCategoryLabels[category]}
                  value={category}
                  key={category}
                />
              ))}
            </Tabs>
          </Box>
        ) : (
          <Select
            value={selectedCategory}
            onChange={(e) =>
              setSelectedCategory(e.target.value as FinancialCategoryEnumModel)
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            {financialCategories.map((category) => (
              <MenuItem value={category} key={category}>
                {financialCategoryLabels[category]}
              </MenuItem>
            ))}
          </Select>
        )}

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="h6" component="div">
                      {financialCategoryLabels[selectedCategory]} Trend Analysis
                    </Typography>
                    {financialCategoryDescriptions[selectedCategory] && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontStyle: "italic" }}
                      >
                        {financialCategoryDescriptions[selectedCategory]}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FinancialLineChartWrapper
                    category={selectedCategory}
                    selectedYearIds={selectedYearIds}
                    yearText={selectedYearText}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default FinancialTrends;
